import { Search, X } from "lucide-react";
import * as React from "react";

import type { DealDashboardMetrics, DealListItem } from "@/api/client";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  EMPTY_DEAL_FILTERS,
  matchesDealFilters,
  type DealAdvancedFilterState,
} from "./dealFilters";
import { DealAdvancedFilters } from "./DealAdvancedFilters";
import {
  DEAL_TAB_LABEL,
  DEAL_TAB_ORDER,
  dealMatchesTab,
  dealTabCount,
  type DealTab,
} from "./dealTabs";
import { DealsDataGrid, type PartnerDealPresetSort } from "./DealsDataGrid";
import { useDealSearchDebounce } from "./useDealSearchDebounce";

/** Minimum query length before search takes over from the tab filter. */
const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;

/** CHP-364: fixed status order for the "group by status" sort (rows of the same
 * status become contiguous). */
const STATUS_GROUP_ORDER: DealListItem["deal_status"][] = [
  "live",
  "extension_pending",
  "pending_review",
  "win_pending_review",
  "closed_won",
  "closed_lost",
  "denied",
  "draft",
];

export interface DealWorkspaceProps {
  /** The caller's full deal set; filtered client-side by tab/search. */
  deals: DealListItem[];
  /** Dashboard metrics for the tab bracket counts (optional while loading). */
  metrics?: DealDashboardMetrics;
  activeTab: DealTab;
  onTabChange: (tab: DealTab) => void;
  /**
   * Search box value, owned by the route so a scorecard click can clear it
   * (otherwise selecting a tab while searching would be silently ignored).
   */
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Header's "Search deals" focuses this input. */
  searchRef: React.RefObject<HTMLInputElement | null>;
  onDealClick: (dealId: string) => void;
  /**
   * CHP-370: Advanced Filters state. The workspace owns it internally by
   * default, but the partner route lifts it so a scorecard ("Closed YTD") can
   * drive it alongside the tab. Controlled when both are supplied; pass both
   * or neither.
   */
  filters?: DealAdvancedFilterState;
  onFiltersChange?: (filters: DealAdvancedFilterState) => void;
  /**
   * Optional CTA shown in the table's empty state when the caller has no deals
   * at all (not when a tab/search merely filters to nothing) — e.g. a
   * "Register a deal" / "Go to Training" button. Conventions sweep review-r2.
   */
  emptyAction?: React.ReactNode;
  /**
   * CHP-891/892: training-aware "no deals yet" zero-state. When the caller is
   * blocked by outstanding mandatory training, the empty state explains that
   * training is the precondition; otherwise it invites the first registration.
   * Defaults to false (the post-training invitation copy).
   */
  trainingBlocked?: boolean;
  /**
   * CHP-1038: sort preset from a scorecard click-through (e.g. expiring-deals
   * → Live sorted soonest-expiry-first). The grid is keyed on it, so setting a
   * new preset remounts the grid with that sort applied; the user can still
   * re-sort from there.
   */
  defaultSort?: PartnerDealPresetSort;
}

/**
 * Tabbed, searchable workspace. Five tabs (Live / In review / Closed-won /
 * Declined / Closed-Lost — CHP-976 split the old Archive tab) filter the list
 * by status group; typing ≥3 characters bypasses the tabs and searches across
 * all statuses ("All Results"). Clearing the search returns to the My Active
 * tab.
 *
 * Filtering is client-side over the caller's own deals — the set is small
 * (a partner's deals), so this avoids per-tab requests and a backend
 * multi-status param (decision recorded in docs/deals/progress.md).
 */
export function DealWorkspace({
  deals,
  metrics,
  activeTab,
  onTabChange,
  searchValue,
  onSearchChange,
  searchRef,
  onDealClick,
  filters: controlledFilters,
  onFiltersChange,
  emptyAction,
  trainingBlocked = false,
  defaultSort,
}: DealWorkspaceProps) {
  const { query, isSearching } = useDealSearchDebounce(searchValue, {
    minChars: SEARCH_MIN_CHARS,
    ms: SEARCH_DEBOUNCE_MS,
  });

  // Advanced Filters v1 (gap-build W2): owned here by default. CHP-370 lets
  // the partner route lift it so a scorecard can drive filters too — when the
  // route supplies `filters` + `onFiltersChange`, this falls back to them.
  const [internalFilters, setInternalFilters] =
    React.useState<DealAdvancedFilterState>(EMPTY_DEAL_FILTERS);
  const filters = controlledFilters ?? internalFilters;
  const setFilters = onFiltersChange ?? setInternalFilters;
  // CHP-364: the clickable Status header groups rows by status; a tab change
  // (or re-click) resets to the default order.
  const [groupByStatus, setGroupByStatus] = React.useState(false);

  function handleTabChange(value: string) {
    if (!value) return;
    setGroupByStatus(false);
    onSearchChange("");
    onTabChange(value as DealTab);
  }

  function handleSearchChange(value: string) {
    onSearchChange(value);
    // Clearing the box returns to the default My Active tab (spec).
    if (value.trim() === "") onTabChange("active");
  }

  const tabOrSearchDeals = isSearching
    ? deals.filter(
        (d) =>
          (d.deal_id_display ?? "").toLowerCase().includes(query) ||
          d.client_name.toLowerCase().includes(query) ||
          (d.company_name ?? "").toLowerCase().includes(query) ||
          (d.owner_name ?? "").toLowerCase().includes(query),
      )
    : deals.filter((d) => dealMatchesTab(d, activeTab));

  // Advanced filters compose (AND) with the tab/search result. Submitted =
  // registration date (created_at); Closing mirrors the Close/Expiry cell
  // (closed_at for terminal rows, target_close_date otherwise).
  const filteredDeals = tabOrSearchDeals.filter((d) =>
    matchesDealFilters(filters, {
      submittedAt: d.created_at,
      closingAt: d.closed_at ?? d.target_close_date,
      valueCents: d.total_contract_value,
    }),
  );

  // CHP-364: group by status = a stable sort by a fixed status order so rows of
  // the same status are contiguous ("all Closed-lost", "all Declined", …).
  const displayDeals = groupByStatus
    ? [...filteredDeals].sort(
        (a, b) =>
          STATUS_GROUP_ORDER.indexOf(a.deal_status) - STATUS_GROUP_ORDER.indexOf(b.deal_status),
      )
    : filteredDeals;

  // CHP-1032/CHP-976: both halves of the old Archive tab keep the archive
  // column set (Date archived replaces Registration/Expiry date).
  const archiveView = (activeTab === "declined" || activeTab === "lost") && !isSearching;
  // CHP-1040: the Closed-won tab swaps Expiry date for Closed-won date.
  const wonView = activeTab === "won" && !isSearching;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={isSearching ? "" : activeTab} onValueChange={handleTabChange}>
          <TabsList>
            {DEAL_TAB_ORDER.map((tab) => (
              <TabsTrigger key={tab} value={tab} data-testid={`deal-tab-${tab}`}>
                {DEAL_TAB_LABEL[tab]}
                {metrics ? (
                  <span className="ml-1.5 tabular-nums text-muted-foreground">
                    ({dealTabCount(metrics, tab)})
                  </span>
                ) : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="relative w-full sm:w-72">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              ref={searchRef}
              type="search"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search deals"
              aria-label="Filter deals"
              // Native webkit cancel button hidden — the explicit X below
              // replaces it cross-browser (Firefox has no native one).
              className="pl-9 pr-9 [&::-webkit-search-cancel-button]:hidden"
            />
            {searchValue !== "" && (
              <button
                type="button"
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  // Same path as typing the box empty (returns to My Active).
                  handleSearchChange("");
                  searchRef.current?.focus();
                }}
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
          <DealAdvancedFilters value={filters} onChange={setFilters} />
        </div>
      </div>

      <div className="mt-4">
        {isSearching && (
          <p className="mb-3 text-sm font-semibold text-muted-foreground">
            All Results ({filteredDeals.length})
          </p>
        )}
        <DealsDataGrid
          // Search results span every tab, so searching keeps the default
          // columns. Keyed so the sort state resets when the column set
          // changes (a lingering sort on a column the other view doesn't
          // render would be invisible) and re-applies when a scorecard sets a
          // new sort preset (CHP-1038 — useTableSort seeds state at mount).
          key={`${archiveView ? "archive" : wonView ? "won" : "default"}:${
            defaultSort ? `${defaultSort.column}-${defaultSort.direction}` : "none"
          }`}
          archiveView={archiveView}
          wonView={wonView}
          defaultSort={defaultSort}
          deals={displayDeals}
          onDealClick={onDealClick}
          emptyMessage={
            isSearching
              ? "No deals match your search."
              : deals.length === 0
                ? // CHP-891/892: training-aware "no deals yet" headline.
                  trainingBlocked
                  ? "Complete training to register deals"
                  : "Register your first deal"
                : "No deals in this view."
          }
          emptyHint={
            !isSearching && deals.length === 0
              ? trainingBlocked
                ? "Mandatory training must be completed before you can register your first deal."
                : "Deals you register will appear here for tracking and updates."
              : undefined
          }
          emptyAction={!isSearching && deals.length === 0 ? emptyAction : undefined}
          statusGrouped={groupByStatus}
          onToggleStatusGroup={() => setGroupByStatus((v) => !v)}
        />
      </div>
    </div>
  );
}
